// src/pages/admin/EmbryoDetails.tsx
import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, Video, Play } from "lucide-react";
import Header from "../../components/layouts/header";
import ApiService from "../../services/api";

interface Embryo {
  _id: string;
  uuid: string;
  CreatedAt: string;
  es: {
    embryo_id: string;
    EmbryoDetails: {
      EmbryoDescriptionID: string;
      EmbryoFate: string;
      FertilizationTime: string;
      InstrumentNumber: string;
      Position: string;
      WellNumber: string;
    };
  };
  [key: string]: any;
}

interface Treatment {
  _id: string;
  name: string;
}

const EmbryoDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const patientIDX = location.state?.patientIDX || "";

  const [embryos, setEmbryos] = useState<Embryo[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [loadingVideo, setLoadingVideo] = useState(false);
  const pageSize = 15;

  useEffect(() => {
    const fetchData = async () => {
      await fetchSamples();
      await fetchTreatments();
    };
    fetchData();
  }, [page, id, selectedTreatment]);

  const fetchTreatments = async () => {
    try {
      const data = await ApiService.fetchTreatments(id!);
      setTreatments(data?.data || []);
    } catch (err) {
      console.error("Failed to fetch treatments:", err);
    }
  };

  const fetchSamples = async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page,
        pageSize,
        patient_id: id,
      };
      if (selectedTreatment) {
        params.evaluation_id = selectedTreatment;
      }

      const data = await ApiService.fetchSamples(params);
      setEmbryos(data?.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to fetch samples:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVideoUrl = async (sample_uuid: string) => {
    setIsVideoOpen(true);
    setLoadingVideo(true);
    setVideoUrl("");

    try {
      const data = await ApiService.fetchVideoUrl(sample_uuid);
      setVideoUrl(data?.video_url || "");
    } catch (error) {
      console.error("Failed to fetch video URL:", error);
    } finally {
      setLoadingVideo(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button & Patient Info */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/records")}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Patients
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Embryo Records
            </h2>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Patient ID:</span>{" "}
                <span className="font-semibold text-gray-900">{id}</span>
              </p>
              <p>
                <span className="font-medium">Patient IDX:</span>{" "}
                <span className="font-semibold text-gray-900">
                  {patientIDX}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Treatment Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Filter by Treatment
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedTreatment}
              onChange={(e) => {
                setSelectedTreatment(e.target.value);
                setPage(1);
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Treatments</option>
              {treatments.map((treatment) => (
                <option key={treatment._id} value={treatment._id}>
                  {treatment.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                setSelectedTreatment("");
                setPage(1);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Clear Filter
            </button>
          </div>
        </div>

        {/* Embryo List */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Embryos ({total})
            </h3>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading embryos...</p>
            </div>
          ) : embryos.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <p className="text-gray-500">No Information Recorded.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Embryo ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Embryo Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {embryos.map((embryo) => (
                      <tr
                        key={embryo._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {embryo._id}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {embryo.es.embryo_id}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs text-gray-600 space-y-1">
                            <p>
                              <span className="font-medium">
                                Description ID:
                              </span>{" "}
                              {embryo.es.EmbryoDetails.EmbryoDescriptionID}
                            </p>
                            <p>
                              <span className="font-medium">Fate:</span>{" "}
                              {embryo.es.EmbryoDetails.EmbryoFate}
                            </p>
                            <p>
                              <span className="font-medium">
                                Fertilization:
                              </span>{" "}
                              {embryo.es.EmbryoDetails.FertilizationTime}
                            </p>
                            <p>
                              <span className="font-medium">Instrument:</span>{" "}
                              {embryo.es.EmbryoDetails.InstrumentNumber}
                            </p>
                            <p>
                              <span className="font-medium">Position:</span>{" "}
                              {embryo.es.EmbryoDetails.Position}
                            </p>
                            <p>
                              <span className="font-medium">Well:</span>{" "}
                              {embryo.es.EmbryoDetails.WellNumber}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(embryo.CreatedAt).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() =>
                                navigate(`/embryo-images/${embryo.uuid}`, {
                                  state: { embryo_id: embryo.es.embryo_id },
                                })
                              }
                              className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-xs font-medium"
                              title="View Images"
                            >
                              <ImageIcon className="h-4 w-4 mr-1" />
                              Images
                            </button>
                            <button
                              onClick={() => fetchVideoUrl(embryo.uuid)}
                              className="inline-flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-xs font-medium"
                              title="Play Video"
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Video
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {embryos.map((embryo) => (
                  <div
                    key={embryo._id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Embryo ID
                          </p>
                          <p className="text-base font-bold text-gray-900 mt-1">
                            {embryo.es.embryo_id}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                          ID
                        </p>
                        <p className="text-sm text-gray-900 truncate">
                          {embryo._id}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                          Embryo Details
                        </p>
                        <div className="text-xs text-gray-600 space-y-1 bg-gray-50 p-3 rounded-md">
                          <p>
                            <span className="font-medium">Description ID:</span>{" "}
                            {embryo.es.EmbryoDetails.EmbryoDescriptionID}
                          </p>
                          <p>
                            <span className="font-medium">Fate:</span>{" "}
                            {embryo.es.EmbryoDetails.EmbryoFate}
                          </p>
                          <p>
                            <span className="font-medium">Fertilization:</span>{" "}
                            {embryo.es.EmbryoDetails.FertilizationTime}
                          </p>
                          <p>
                            <span className="font-medium">Instrument:</span>{" "}
                            {embryo.es.EmbryoDetails.InstrumentNumber}
                          </p>
                          <p>
                            <span className="font-medium">Position:</span>{" "}
                            {embryo.es.EmbryoDetails.Position}
                          </p>
                          <p>
                            <span className="font-medium">Well:</span>{" "}
                            {embryo.es.EmbryoDetails.WellNumber}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Created
                        </p>
                        <p className="text-sm text-gray-900 mt-1">
                          {new Date(embryo.CreatedAt).toLocaleString()}
                        </p>
                      </div>

                      <div className="flex space-x-2 pt-2">
                        <button
                          onClick={() =>
                            navigate(`/embryo-images/${embryo.uuid}`, {
                              state: { embryo_id: embryo.es.embryo_id },
                            })
                          }
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <ImageIcon className="h-4 w-4 mr-2" />
                          View Images
                        </button>
                        <button
                          onClick={() => fetchVideoUrl(embryo.uuid)}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Play Video
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-sm text-gray-700 font-medium">
                  Page: <span className="font-bold">{page}</span> /{" "}
                  <span className="font-bold">{totalPages}</span> | Total:{" "}
                  <span className="font-bold">{total}</span> records
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ◀ Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next ▶
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full relative">
            <button
              onClick={() => {
                setIsVideoOpen(false);
                setVideoUrl("");
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 bg-white rounded-full p-2 shadow-lg z-10"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="p-6">
              {loadingVideo ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading video...</p>
                </div>
              ) : videoUrl ? (
                <video
                  controls
                  autoPlay
                  className="w-full rounded-lg"
                  style={{ maxHeight: "70vh" }}
                >
                  <source src={videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="text-center py-12">
                  <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Video not available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbryoDetails;
